import { create, muiDialogV5, useModal } from '@ebay/nice-modal-react'
import React, { useState } from 'react'
import { Box, Button as MuiButton, InputAdornment, Typography, styled } from '@mui/material'
import { useNetwork } from 'wagmi'
import { parseUnits } from 'ethers/lib/utils'
import { useBoolean, useCountDown } from 'ahooks'
import { BigNumber as EthersBigNumber } from 'ethers'
import dayjs, { unix } from 'dayjs'
import Check from '../Check'
import Dialog from '@/components/common/Dialog'
import { FlexCol } from '@/components/common/Columns'
import NumberInput from '@/components/common/NumberInput'
import { MaxButton } from '@/components/common/MaxButton'
import { SupportedTicker, TICKERS_MAP_BY_NAME } from '@/constants/token'
import { formatNumber, getTokenDecimals } from '@/utils'
import { useUnstake } from '@/hooks/farmHooks'

const Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px 4px;

  background: rgba(191, 255, 11, 0.1);
  border-radius: 4px;

  margin-right: 4px;

  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  font-feature-settings: 'ss01' on, 'case' on, 'calt' off;
`

export const MinimumAmountWrapper = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: 8px;
  align-self: flex-end;
`
export const Text3 = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`

const Button = styled(MuiButton)`
  height: 48px;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`

const UnstakeDialog = ({
  tickerName,
  poolId,
  onSuccess,
  stakedAmount,
  endAt,
  lockTime,
}: {
  tickerName: SupportedTicker
  poolId: number
  onSuccess: () => void
  stakedAmount?: EthersBigNumber
  endAt: number
  lockTime: number
}) => {
  const modal = useModal()

  const [amountInput, setAmountInput] = useState('')

  const { chain } = useNetwork()

  const decimal = getTokenDecimals(tickerName, chain?.id)

  const [isLocked, setIsLocked] = useState(unix(endAt).isAfter(dayjs()))

  const [, formattedRes] = useCountDown({
    targetDate: endAt * 1000,
    onEnd: () => {
      setIsLocked(false)
    },
  })

  const { days, hours, minutes, seconds } = formattedRes

  const { run: unstake, loading: isUnstaking } = useUnstake(
    poolId,
    amountInput ? parseUnits(amountInput, decimal) : '0',
    {
      onSuccess: () => {
        setTimeout(() => {
          onSuccess()
        }, 500)
        modal.hide()
      },
    },
  )

  const isBalanceInsufficient = stakedAmount.lt(parseUnits(amountInput || '0', decimal))

  const [isChecked, { toggle: toggleChecked }] = useBoolean()

  return (
    <Dialog {...muiDialogV5(modal)}>
      <FlexCol sx={{ rowGap: { xs: 24, md: 40 }, minWidth: { xs: 'auto', md: 400 } }}>
        <Typography fontSize={{ xs: 24, md: 32 }}>Unstake {tickerName}</Typography>

        <FlexCol sx={{ rowGap: { xs: 12, md: 20 } }}>
          <MinimumAmountWrapper>
            <Text3 sx={{ opacity: 0.5 }}>Available</Text3>
            <Text3>
              {stakedAmount ? formatNumber(stakedAmount.toString(), decimal) : '-'}{' '}
              {TICKERS_MAP_BY_NAME?.[tickerName].erc20Tokens?.[chain?.id]?.symbol}
            </Text3>
          </MinimumAmountWrapper>

          <NumberInput
            value={amountInput}
            onUserInput={(value) => {
              if (value.split('.')?.[1] && value.split('.')?.[1]?.length > 6) {
                return
              } else {
                setAmountInput(value)
              }
            }}
            startAdornment={
              <InputAdornment position="start">
                <Tag>Amount</Tag>
              </InputAdornment>
            }
            endAdornment={
              <MaxButton
                onClick={() => {
                  if (stakedAmount && typeof decimal === 'number') {
                    setAmountInput(formatNumber(stakedAmount.toString(), decimal, { shouldCommify: false }))
                  }
                }}
              />
            }
          />

          {isLocked && (
            <Typography sx={{ textAlign: 'end' }}>
              Lock until
              <Typography component="span" color="#BFFF0B">
                &nbsp;{days} d {hours} h {minutes} m {seconds} s&nbsp;
              </Typography>
              later
            </Typography>
          )}

          <Check value={isChecked} toggle={toggleChecked} lockTime={lockTime} action="unstake" />

          <Button
            variant="contained"
            onClick={unstake}
            disabled={isUnstaking || isLocked || !amountInput || isBalanceInsufficient || !isChecked}
          >
            {isBalanceInsufficient ? 'Insufficient Balance' : isUnstaking ? 'Unstaking...' : 'Unstake'}
          </Button>
        </FlexCol>
      </FlexCol>
    </Dialog>
  )
}

export default create(UnstakeDialog)
